import { useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ref, onValue, push, update } from "firebase/database";
import { db } from "../../configs/Firebase";
import TitleBar from "../../designSystem/c-organism/titleBar/TitleBar";
import Button from "../../designSystem/b-molecules/buttons/Button";
import Tabs from "../../designSystem/b-molecules/tabs/Tabs";
import Select from "../../designSystem/b-molecules/formElements/select/Select";
import UploadDialog from "./UploadDialog";
import ThemeChart from "./components/ThemeChart";
import ThemeDetail from "./ThemeDetail";
import UploadDetail from "./UploadDetail";
import {
  getAllRecords,
  filterByDateRange,
  filterByBrand,
  filterBySource,
  filterByCountry,
  filterBySentiment,
  getDateRangeForPeriod,
  groupByTheme,
  addPercentages,
  prepareBarChartData,
  preparePieChartData,
  calculateAverageRating,
  calculateSentimentSummary
} from "./utils/processData";
import { categorizeFeedback, generateThemeId } from "./utils/categorizeFeedback";
import { setUploads, setThemes, setBrands, setCountries, setSelectedPeriod, setChartType, setCustomDateRange, setSelectedBrand, setSelectedSource, setSelectedCountry, toggleSentiment } from "../../features/customersSlice";
import DateRangePicker from "./components/DateRangePicker";
import ManageBrands from "./components/ManageBrands";
import ManageCountries from "./components/ManageCountries";
import ManageSources from "./components/ManageSources";
import ManageThemes from "./components/ManageThemes";
import Dropdown from "../../designSystem/c-organism/dialogs/Dropdown";
import Icon from "../../designSystem/a-atoms/icons/Icon";
import style from "./Customers.module.css";
import Dialog from "../../designSystem/c-organism/dialogs/Dialog";

const Customers = () => {
  const org = useSelector((state) => state.orgStatus.value);
  const userStatus = useSelector((state) => state.userStatus.value);
  const customersState = useSelector((state) => state.customers);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [user, setUser] = useState(false);
  const [uploads, setUploadsLocal] = useState([]);
  const [themes, setThemesLocal] = useState([]);
  const [brands, setBrandsLocal] = useState([]);
  const [countries, setCountriesLocal] = useState([]);
  const [sources, setSourcesLocal] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [averageRating, setAverageRating] = useState(null);
  const [sentimentSummary, setSentimentSummary] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showManageBrands, setShowManageBrands] = useState(false);
  const [showManageCountries, setShowManageCountries] = useState(false);
  const [showManageSources, setShowManageSources] = useState(false);
  const [showManageThemes, setShowManageThemes] = useState(false);
  const [categorizing, setCategorizing] = useState(false);
  const [categorizationProgress, setCategorizationProgress] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [optionsStyle, setOptionsStyle] = useState({});

  useEffect(() => {
    if (userStatus) {
      setUser(JSON.parse(userStatus.payload));
    }
  }, [userStatus]);

  useEffect(() => {
    if (org.payload) {
      // Load uploads
      const uploadsRef = ref(db, `userData/${org.payload}/customers/data`);
      onValue(uploadsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const uploadsList = Object.keys(data).map(id => ({
            id,
            ...data[id]
          }));
          
          // Sort by uploadedAt timestamp (newest first)
          uploadsList.sort((a, b) => {
            const timestampA = a.uploadedAt || 0;
            const timestampB = b.uploadedAt || 0;
            return timestampB - timestampA; // Descending order (newest first)
          });
          
          setUploadsLocal(uploadsList);
          dispatch(setUploads(uploadsList));
        } else {
          setUploadsLocal([]);
          dispatch(setUploads([]));
        }
      });

      // Load themes
      const themesRef = ref(db, `userData/${org.payload}/customers/themes`);
      onValue(themesRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const themesList = Object.keys(data).map(id => ({
            id,
            ...data[id]
          }));
          setThemesLocal(themesList);
          dispatch(setThemes(themesList));
        } else {
          setThemesLocal([]);
          dispatch(setThemes([]));
        }
      });

      // Load brands
      const brandsRef = ref(db, `userData/${org.payload}/brands`);
      onValue(brandsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const brandsList = Object.keys(data).map(id => ({
            id,
            ...data[id]
          }));
          setBrandsLocal(brandsList);
          dispatch(setBrands(brandsList));
        } else {
          setBrandsLocal([]);
          dispatch(setBrands([]));
        }
      });

      // Load countries
      const countriesRef = ref(db, `userData/${org.payload}/countries`);
      onValue(countriesRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const countriesList = Object.keys(data).map(id => ({
            id,
            ...data[id]
          }));
          setCountriesLocal(countriesList);
          dispatch(setCountries(countriesList));
        } else {
          setCountriesLocal([]);
          dispatch(setCountries([]));
        }
      });

      // Load sources
      const sourcesRef = ref(db, `userData/${org.payload}/customers/sources`);
      onValue(sourcesRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const sourcesList = Object.keys(data).map(id => ({
            id,
            ...data[id]
          }));
          setSourcesLocal(sourcesList);
        } else {
          setSourcesLocal([]);
        }
      });
    }
  }, [org.payload, dispatch]);

  useEffect(() => {
    // Process data for charts
    if (uploads.length > 0) {
      const allRecords = getAllRecords(uploads);
      
      // Apply brand filter
      let filteredByBrand = customersState.selectedBrand 
        ? filterByBrand(allRecords, customersState.selectedBrand)
        : allRecords;
      
      // Apply country filter
      let filteredByCountry = customersState.selectedCountry
        ? filterByCountry(filteredByBrand, customersState.selectedCountry)
        : filteredByBrand;
      
      // Apply source filter
      let filteredBySource = customersState.selectedSource
        ? filterBySource(filteredByCountry, customersState.selectedSource)
        : filteredByCountry;
      
      // Apply date range filter
      const period = customersState.selectedPeriod || "30";
      const dateRange = getDateRangeForPeriod(period, customersState.customDateRange);

      if (dateRange) {
        const filteredByDate = filterByDateRange(filteredBySource, dateRange.start, dateRange.end);
        
        // Calculate average rating (from all filtered records, not sentiment-filtered)
        const ratingData = calculateAverageRating(filteredByDate);
        setAverageRating(ratingData);

        // Calculate sentiment summary (from all filtered records, not sentiment-filtered)
        const sentimentData = calculateSentimentSummary(filteredByDate);
        setSentimentSummary(sentimentData);

        // Apply sentiment filter for chart data
        const filteredBySentiment = filterBySentiment(filteredByDate, customersState.selectedSentiments);

        const themeGroups = groupByTheme(filteredBySentiment);
        const themeGroupsWithPercentages = addPercentages(themeGroups, filteredBySentiment.length);

        if (customersState.chartType === "pie") {
          const pieData = preparePieChartData(themeGroupsWithPercentages);
          setChartData(pieData);
        } else {
          const barData = prepareBarChartData(themeGroupsWithPercentages);
          setChartData(barData);
        }
      } else {
        setChartData([]);
        setAverageRating(null);
        setSentimentSummary(null);
      }
    } else {
      setChartData([]);
      setAverageRating(null);
      setSentimentSummary(null);
    }
  }, [uploads, customersState.selectedPeriod, customersState.chartType, customersState.customDateRange, customersState.selectedBrand, customersState.selectedSource, customersState.selectedCountry, customersState.selectedSentiments]);

  async function handleUploadComplete(uploadId, records) {
    setCategorizing(true);
    setCategorizationProgress("Categorizing feedback...");

    try {
      // Categorize feedback - this now returns ALL records (those with text get categorized, those without get "Uncategorized")
      const categorized = await categorizeFeedback(records);

      // Create theme map
      const themeMap = {};
      categorized.forEach(item => {
        if (!themeMap[item.theme]) {
          themeMap[item.theme] = {
            id: generateThemeId(item.theme),
            name: item.theme,
            records: []
          };
        }
        themeMap[item.theme].records.push(item.id);
      });

      // Update records with themes and sentiment - process ALL records including those without text
      const updates = {};
      categorized.forEach(item => {
        const record = records.find(r => r.id === item.id);
        if (record) {
          const themeId = generateThemeId(item.theme);
          updates[`userData/${org.payload}/customers/data/${uploadId}/records/${item.id}/theme`] = themeId;
          updates[`userData/${org.payload}/customers/data/${uploadId}/records/${item.id}/themeName`] = item.theme;
          // Store sentiment if available
          if (item.sentiment) {
            updates[`userData/${org.payload}/customers/data/${uploadId}/records/${item.id}/sentiment`] = item.sentiment;
          }
          // Preserve sub-theme fields if they exist (don't overwrite manually assigned sub-themes)
          if (record.subTheme) {
            updates[`userData/${org.payload}/customers/data/${uploadId}/records/${item.id}/subTheme`] = record.subTheme;
          }
          if (record.subThemeName) {
            updates[`userData/${org.payload}/customers/data/${uploadId}/records/${item.id}/subThemeName`] = record.subThemeName;
          }
        }
      });

      // Ensure all records have a theme assigned (safety check for any records that might have been missed)
      records.forEach(record => {
        const categorizedItem = categorized.find(c => c.id === record.id);
        if (!categorizedItem) {
          // If a record wasn't in categorized results, assign "Uncategorized"
          const themeId = generateThemeId("Uncategorized");
          updates[`userData/${org.payload}/customers/data/${uploadId}/records/${record.id}/theme`] = themeId;
          updates[`userData/${org.payload}/customers/data/${uploadId}/records/${record.id}/themeName`] = "Uncategorized";
          // Default to neutral sentiment for uncategorized records
          updates[`userData/${org.payload}/customers/data/${uploadId}/records/${record.id}/sentiment`] = "neutral";
          
          // Preserve sub-theme fields if they exist
          if (record.subTheme) {
            updates[`userData/${org.payload}/customers/data/${uploadId}/records/${record.id}/subTheme`] = record.subTheme;
          }
          if (record.subThemeName) {
            updates[`userData/${org.payload}/customers/data/${uploadId}/records/${record.id}/subThemeName`] = record.subThemeName;
          }
          
          // Add to theme map
          if (!themeMap["Uncategorized"]) {
            themeMap["Uncategorized"] = {
              id: themeId,
              name: "Uncategorized",
              records: []
            };
          }
          themeMap["Uncategorized"].records.push(record.id);
        } else if (!categorizedItem.sentiment) {
          // Ensure sentiment is set even if missing
          updates[`userData/${org.payload}/customers/data/${uploadId}/records/${record.id}/sentiment`] = "neutral";
        }
      });

      // Create/update themes
      const themeUpdates = {};
      Object.values(themeMap).forEach(theme => {
        themeUpdates[`userData/${org.payload}/customers/themes/${theme.id}/name`] = theme.name;
        themeUpdates[`userData/${org.payload}/customers/themes/${theme.id}/createdAt`] = { ".sv": "timestamp" };
        themeUpdates[`userData/${org.payload}/customers/themes/${theme.id}/recordCount`] = theme.records.length;
      });

      // Update all records and themes
      await update(ref(db), { ...updates, ...themeUpdates });

      setCategorizationProgress("Categorization complete!");
      setTimeout(() => {
        setCategorizing(false);
        setCategorizationProgress("");
      }, 1000);
    } catch (error) {
      console.error("Error categorizing:", error);
      setCategorizationProgress("Error during categorization. Please try again.");
      setTimeout(() => {
        setCategorizing(false);
        setCategorizationProgress("");
      }, 2000);
    }
  }

  function handlePeriodChange(e, index) {
    const periods = ["7", "lastWeek", "30", "lastMonth", "90", "custom"];
    dispatch(setSelectedPeriod(periods[index]));
  }

  function handleCustomDateRangeChange(dateRange) {
    dispatch(setCustomDateRange(dateRange));
  }

  function handleClearCustomDateRange() {
    dispatch(setCustomDateRange(null));
    dispatch(setSelectedPeriod("30"));
  }

  function handleChartTypeChange(type) {
    dispatch(setChartType(type));
  }

  function showOptionsHandler(e) {
    setShowOptions(true);
    setOptionsStyle({
      left: "auto",
      right: window.innerWidth - e.currentTarget.getBoundingClientRect().right,
      top: e.currentTarget.getBoundingClientRect().top,
    });
  }

  function closeOptionsHandler() {
    setShowOptions(false);
  }

  function handleThemeClick(themeName) {
    // Find theme ID from name
    let themeId = null;
    
    // First try to find in themes list
    const theme = themes.find(t => t.name === themeName);
    if (theme) {
      themeId = theme.id;
    } else {
      // Otherwise, search through records to find theme ID
      for (const upload of uploads) {
        if (upload.records && Array.isArray(upload.records)) {
          const record = upload.records.find(r => r.themeName === themeName);
          if (record && record.theme) {
            themeId = record.theme;
            break;
          }
        }
      }
    }
    
    // If still not found, generate ID from name
    if (!themeId && themeName) {
      themeId = themeName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }
    
    if (themeId) {
      navigate(`/customers/theme/${themeId}`);
    }
  }

  const periodOptions = [
    { value: "Last 7 days" },
    { value: "Last Week" },
    { value: "Last 30 days" },
    { value: "Last Month" },
    { value: "Last 90 days" },
    { value: "Custom" }
  ];

  const currentPeriodIndex = ["7", "lastWeek", "30", "lastMonth", "90", "custom"].indexOf(customersState.selectedPeriod || "30");
  const isCustomPeriod = customersState.selectedPeriod === "custom";

  return (
    <Routes>
      <Route path="/">
        <Route
          index
          element={
            <div className="mainContentInner">
              <TitleBar
                title="Customers"
                actions={[
                  {
                    icon: "more_vert",
                    name: "",
                    action: showOptionsHandler,
                    variants: ["text"]
                  }
                ]}
                breadcrumb={[{ name: "Home", url: "/" }]}
              />
              <Dropdown
                show={showOptions}
                onClose={closeOptionsHandler}
                style={optionsStyle}
              >
                <ul className={style.optionsMenu}>
                  <li>
                    <Button
                      preIcon="upload"
                      onClick={() => {
                        setShowUploadDialog(true);
                        closeOptionsHandler();
                      }}
                      variants={["text"]}
                    >
                      Upload CSV
                    </Button>
                  </li>
                  <li>
                    <Button
                      preIcon="label"
                      onClick={() => {
                        setShowManageBrands(true);
                        closeOptionsHandler();
                      }}
                      variants={["text"]}
                    >
                      Manage Brands
                    </Button>
                  </li>
                  <li>
                    <Button
                      preIcon="public"
                      onClick={() => {
                        setShowManageCountries(true);
                        closeOptionsHandler();
                      }}
                      variants={["text"]}
                    >
                      Manage Countries
                    </Button>
                  </li>
                  <li>
                    <Button
                      preIcon="source"
                      onClick={() => {
                        setShowManageSources(true);
                        closeOptionsHandler();
                      }}
                      variants={["text"]}
                    >
                      Manage Sources
                    </Button>
                  </li>
                  <li>
                    <Button
                      preIcon="category"
                      onClick={() => {
                        setShowManageThemes(true);
                        closeOptionsHandler();
                      }}
                      variants={["text"]}
                    >
                      Manage Themes
                    </Button>
                  </li>
                </ul>
              </Dropdown>
              <div className="contentPadding">
                <div className={style.topSummary}>
                {averageRating && averageRating.count > 0 && (
                    <div>
                  <div className={style.ratingDisplay}>
                    <div className={style.sentimentTitle}>
                      Average Rating
                    </div>
                    <div className={style.sentimentTotal}>
                      (based on {averageRating.count} rating{averageRating.count !== 1 ? 's' : ''})
                    </div>
                  </div>
                  <div className={style.ratingValue}>{averageRating.formatted}</div>
                  </div>
                )}

                {sentimentSummary && sentimentSummary.total > 0 && (
                  <div className={style.sentimentDisplay}>
                    <div><span className={style.sentimentTitle}>Sentiment Summary</span> <span className={style.sentimentTotal}>({sentimentSummary.total} feedback item{sentimentSummary.total !== 1 ? 's' : ''})</span></div>
                    <div className={style.sentimentBreakdown}>
                      <div 
                        className={`${style.sentimentItem} ${style.positive} ${customersState.selectedSentiments?.includes('positive') ? style.selected : ''}`}
                        onClick={() => dispatch(toggleSentiment('positive'))}
                      >
                        <Icon variants={["inheritColor"]}>sentiment_satisfied</Icon>
                        <div className={style.sentimentDetails}>
                          <div className={style.sentimentPercentage}>{sentimentSummary.positive.percentage}%</div>
                          <div className={style.sentimentCount}>Positive ({sentimentSummary.positive.count})</div>
                        </div>
  
                      </div>
                      <div 
                        className={`${style.sentimentItem} ${style.negative} ${customersState.selectedSentiments?.includes('negative') ? style.selected : ''}`}
                        onClick={() => dispatch(toggleSentiment('negative'))}
                      >
                        <Icon variants={["inheritColor"]}>sentiment_dissatisfied</Icon>
                        <div className={style.sentimentDetails}>
                          <div className={style.sentimentPercentage}>{sentimentSummary.negative.percentage}%</div>
                          <div className={style.sentimentCount}>Negative ({sentimentSummary.negative.count})</div>
                        </div>

                      </div>
                      <div 
                        className={`${style.sentimentItem} ${style.neutral} ${customersState.selectedSentiments?.includes('neutral') ? style.selected : ''}`}
                        onClick={() => dispatch(toggleSentiment('neutral'))}
                      >
                        <Icon variants={["inheritColor"]}>sentiment_neutral</Icon>
                        <div className={style.sentimentDetails}>
                          <div className={style.sentimentPercentage}>{sentimentSummary.neutral.percentage}%</div>
                          <div className={style.sentimentCount}>Neutral ({sentimentSummary.neutral.count})</div>
                        </div>

                      </div>
                    </div>
                  </div>
                )}
</div>
                <div className={style.controls}>
                  <div className={style.filters}>
                    <div className={style.filterGroup}>
                      <Select
                        label="Brand"
                        type="dropdown"
                        options={[{ value: "All Brands" }, ...brands.map(b => ({ value: b.name }))]}
                        selectedOptions={[customersState.selectedBrand ? brands.find(b => b.id === customersState.selectedBrand)?.name || "All Brands" : "All Brands"]}
                        onChange={(e, index) => {
                          if (index === 0) {
                            dispatch(setSelectedBrand(null));
                          } else {
                            dispatch(setSelectedBrand(brands[index - 1].id));
                          }
                        }}
                        multiselect={false}
                      />
                    </div>
                    <div className={style.filterGroup}>
                      <Select
                        label="Country"
                        type="dropdown"
                        options={[{ value: "All Countries" }, ...countries.map(c => ({ value: c.name }))]}
                        selectedOptions={[customersState.selectedCountry ? countries.find(c => c.id === customersState.selectedCountry)?.name || "All Countries" : "All Countries"]}
                        onChange={(e, index) => {
                          if (index === 0) {
                            dispatch(setSelectedCountry(null));
                          } else {
                            dispatch(setSelectedCountry(countries[index - 1].id));
                          }
                        }}
                        multiselect={false}
                      />
                    </div>
                    <div className={style.filterGroup}>
                      {(() => {
                        const fallbackSourceOptions = [
                          { label: "All Sources", key: null },
                          { label: "Customer Service", key: "customer_service" },
                          { label: "Google Play", key: "google_play" },
                          { label: "App Store", key: "app_store" },
                          { label: "Other", key: "other" }
                        ];

                        const dynamicSourceOptions = sources.length > 0
                          ? [{ label: "All Sources", key: null }, ...sources.map(s => ({ label: s.name, key: s.key }))]
                          : fallbackSourceOptions;

                        const currentSourceOption =
                          dynamicSourceOptions.find(opt => opt.key === customersState.selectedSource) ||
                          dynamicSourceOptions[0];

                        return (
                          <Select
                            label="Feedback Type"
                            type="dropdown"
                            options={dynamicSourceOptions.map(opt => ({ value: opt.label }))}
                            selectedOptions={[currentSourceOption.label]}
                            onChange={(e, index) => {
                              const selected = dynamicSourceOptions[index];
                              if (!selected || !selected.key) {
                                dispatch(setSelectedSource(null));
                              } else {
                                dispatch(setSelectedSource(selected.key));
                              }
                            }}
                            multiselect={false}
                          />
                        );
                      })()}
                    </div>
                    <div className={style.periodSelector}>
                      <Select
                        label="Time Period"
                        type="dropdown"
                        options={periodOptions}
                        selectedOptions={[periodOptions[currentPeriodIndex]?.value || "Last 30 days"]}
                        onChange={handlePeriodChange}
                        multiselect={false}
                      />
                    </div>
                  </div>
                  <div className={style.chartTypeToggle}>
                    <Tabs
                    variants={["toggle"]}
                      tabs={[
                        {
                          name: "Bar Chart",
                          selected: customersState.chartType === "bar",
                          onClick: () => handleChartTypeChange("bar")
                        },
                        {
                          name: "Pie Chart",
                          selected: customersState.chartType === "pie",
                          onClick: () => handleChartTypeChange("pie")
                        }
                      ]}
                    />
                  </div>
                </div>

                {isCustomPeriod && (
                  <div className={style.customDateRangeContainer}>
                    <DateRangePicker
                      startDate={customersState.customDateRange?.start || null}
                      endDate={customersState.customDateRange?.end || null}
                      onDateChange={handleCustomDateRangeChange}
                      onClear={handleClearCustomDateRange}
                    />
                  </div>
                )}

                {chartData.length > 0 ? (
                  <div className={style.chartContainer}>
                    <ThemeChart
                      data={chartData}
                      chartType={customersState.chartType || "bar"}
                      onThemeClick={handleThemeClick}
                    />
                  </div>
                ) : (
                  <div className={style.noData}>
                    {uploads.length === 0
                      ? "No customer feedback data uploaded yet. Click 'Upload CSV' to get started."
                      : "No data available for the selected time period."}
                  </div>
                )}

                {uploads.length > 0 && (
                  <div className={style.uploadsList}>
                    <h3>Uploaded Data Sources</h3>
                    <div className={style.uploads}>
                      {uploads.map((upload) => (
                        <div 
                          key={upload.id} 
                          className={style.uploadItem}
                          onClick={() => navigate(`/customers/upload/${upload.id}`)}
                        >
                          <div className={style.uploadInfo}>
                            <div className={style.uploadSource}>
                              {upload.source?.replace("_", " ") || "Unknown"}
                            </div>
                            <div className={style.uploadMeta}>
                              {upload.recordCount || 0} records •{" "}
                              {upload.uploadedAt
                                ? new Date(upload.uploadedAt).toLocaleDateString()
                                : "Unknown date"}
                            </div>
                          </div>
                          <Icon>chevron_right</Icon>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <UploadDialog
                show={showUploadDialog}
                onClose={() => setShowUploadDialog(false)}
                orgId={org.payload}
                userId={user?.uid}
                onUploadComplete={handleUploadComplete}
                brands={brands}
                countries={countries}
                sources={sources}
              />

              <ManageBrands
                show={showManageBrands}
                onClose={() => setShowManageBrands(false)}
              />

              <ManageCountries
                show={showManageCountries}
                onClose={() => setShowManageCountries(false)}
              />

              <ManageSources
                show={showManageSources}
                onClose={() => setShowManageSources(false)}
              />

              <ManageThemes
                show={showManageThemes}
                onClose={() => setShowManageThemes(false)}
              />

              <Dialog show={categorizing} title="Processing" onClose={() => {}}>
                <div className={style.progressDialog}>
                  <p>{categorizationProgress}</p>
                </div>
              </Dialog>
            </div>
          }
        />
        <Route path="theme/:themeId" element={<ThemeDetail />} />
        <Route path="upload/:uploadId" element={<UploadDetail />} />
      </Route>
    </Routes>
  );
};

export default Customers;

